import { useState, useEffect } from "react";

/**
 * Hook to detect when all images in a container are loaded
 * @param {React.RefObject} containerRef - Ref to the container element (optional)
 * @returns {boolean} - true when all images are loaded
 */
export const useImageLoader = (containerRef = null) => {
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [imagesLoading, setImagesLoading] = useState(true);

  useEffect(() => {
    const checkImages = () => {
      // Get images from the container if ref is provided, otherwise check all images
      const container = containerRef?.current;
      const images = container
        ? container.querySelectorAll("img")
        : document.querySelectorAll("img");

      if (images.length === 0) {
        setImagesLoaded(true);
        setImagesLoading(false);
        return;
      }

      let loadedCount = 0;
      const totalImages = images.length;

      const handleImageLoad = () => {
        loadedCount++;
        if (loadedCount === totalImages) {
          setImagesLoaded(true);
          setImagesLoading(false);
        }
      };

      const handleImageError = () => {
        loadedCount++;
        if (loadedCount === totalImages) {
          setImagesLoaded(true);
          setImagesLoading(false);
        }
      };

      // Check if images are already loaded
      images.forEach((img) => {
        if (img.complete && img.naturalHeight !== 0) {
          loadedCount++;
        } else {
          img.addEventListener("load", handleImageLoad);
          img.addEventListener("error", handleImageError);
        }
      });

      // If all images were already loaded
      if (loadedCount === totalImages) {
        setImagesLoaded(true);
        setImagesLoading(false);
      }

      // Cleanup
      return () => {
        images.forEach((img) => {
          img.removeEventListener("load", handleImageLoad);
          img.removeEventListener("error", handleImageError);
        });
      };
    };

    // Small delay to ensure DOM is ready
    const timeout = setTimeout(checkImages, 100);

    // Also check on window load
    window.addEventListener("load", checkImages);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener("load", checkImages);
    };
  }, [containerRef]);

  return { imagesLoaded, imagesLoading };
};
